/**
 * Logger utility for rewards-admin
 * 
 * Provides proper logging with environment-based controls.
 * Using a proper logger instead of console.log provides better
 * control, formatting, and production behavior.
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  prefix?: string;
}

class Logger {
  private config: LoggerConfig;

  constructor(config: LoggerConfig) {
    this.config = config;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enableConsole && level <= this.config.level;
  }

  private formatMessage(level: string, message: string, ...args: any[]): [string, ...any[]] {
    const timestamp = new Date().toISOString();
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';
    return [`${timestamp} ${prefix}[${level}] ${message}`, ...args];
  }

  error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      // eslint-disable-next-line no-console
      console.error(...this.formatMessage('ERROR', message, ...args));
    }
  }

  warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      // eslint-disable-next-line no-console
      console.warn(...this.formatMessage('WARN', message, ...args));
    }
  }

  info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      // eslint-disable-next-line no-console
      console.info(...this.formatMessage('INFO', message, ...args));
    }
  }

  debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      // eslint-disable-next-line no-console
      console.log(...this.formatMessage('DEBUG', message, ...args));
    }
  }
}

// Create application logger instance
const getLogLevel = (): LogLevel => {
  if (process.env.NODE_ENV === 'development') {
    return LogLevel.DEBUG; // Show all logs in development
  }
  if (process.env.NODE_ENV === 'test') {
    return LogLevel.WARN; // Only warnings and errors in tests
  }
  return LogLevel.ERROR; // Only errors in production
};

export const logger = new Logger({
  level: getLogLevel(),
  enableConsole: true,
  prefix: 'REWARDS-ADMIN',
});

// Export default instance
export default logger;
